import type {
  DeviceFeatureStatusResponse,
  SuccessListDeviceFeatureStatusResponse
} from "@9amhealth/openapi";
import {
  SetDeviceFeatureMemberRequest,
  WithingsDeviceFeatureControllerService
} from "@9amhealth/openapi";
import { Cubit } from "blac-next";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { onUserSessionReset } from "src/lib/onUserSessionReset";

/**
 * Feature modes a member can toggle on their Withings scale. The request and
 * response models declare identical enums; the request one is canonical here
 * because it is what we send.
 */
export const WithingsFeatureMode = SetDeviceFeatureMemberRequest.mode;
export type WithingsFeatureMode = SetDeviceFeatureMemberRequest.mode;

export interface WithingsFeaturesState {
  features?: DeviceFeatureStatusResponse[];
  loading: boolean;
}

export default class WithingsFeaturesCubit extends Cubit<WithingsFeaturesState> {
  static keepAlive = true;

  constructor() {
    super({ loading: false });

    onUserSessionReset(() => this.emit({ loading: false }));
  }

  load = async (): Promise<void> => {
    this.patch({ loading: true });

    try {
      const { data }: SuccessListDeviceFeatureStatusResponse =
        await WithingsDeviceFeatureControllerService.fetchMemberDeviceFeatureStatuses();
      this.patch({ features: data, loading: false });
    } catch (error) {
      reportErrorSentry(error);
      this.patch({ features: [], loading: false });
    }
  };

  isAvailable = (mode: WithingsFeatureMode): boolean =>
    this.state.features?.some(
      (feature) => feature.mode === mode && feature.available
    ) ?? false;

  isEnabled = (mode: WithingsFeatureMode): boolean =>
    this.state.features?.some(
      (feature) => feature.mode === mode && feature.enabled
    ) ?? false;

  get bodyCompositionAvailable(): boolean {
    return this.isAvailable(WithingsFeatureMode.BODY_COMPOSITION);
  }

  get eyesClosedAvailable(): boolean {
    return this.isAvailable(WithingsFeatureMode.EYES_CLOSED);
  }

  /**
   * The backend targets one device model per call, and the model to target is
   * whichever one reported the mode in the last load. A mode with no `modelId`
   * has no compatible device behind it, so there is nothing to write: bail
   * before the optimistic patch rather than show a row flipping to a value the
   * scale never received.
   */
  setFeature = async (
    mode: WithingsFeatureMode,
    enabled: boolean
  ): Promise<void> => {
    const modelId = this.featureFor(mode)?.modelId;

    if (modelId === undefined) {
      const error = new Error(
        `No Withings device model available for feature ${mode}`
      );
      reportErrorSentry(error);
      throw error;
    }

    const previous = this.state.features;
    this.patch({ features: this.withFeatureEnabled(mode, enabled) });

    try {
      await WithingsDeviceFeatureControllerService.setMemberDeviceFeature({
        mode,
        enabled,
        modelId
      });
    } catch (error) {
      reportErrorSentry(error);
      this.patch({ features: previous });
      throw error;
    }
  };

  private featureFor = (
    mode: WithingsFeatureMode
  ): DeviceFeatureStatusResponse | undefined =>
    this.state.features?.find((feature) => feature.mode === mode);

  private withFeatureEnabled = (
    mode: WithingsFeatureMode,
    enabled: boolean
  ): DeviceFeatureStatusResponse[] =>
    (this.state.features ?? []).map((feature) =>
      feature.mode === mode ? { ...feature, enabled } : feature
    );
}
